import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LocationToast from './components/LocationToast/LocationToast';
import { useLocationFlow } from './components/Utils/useLocationFlow';
import HeroSection from './components/HeroSection/HeroSection';
import SearchSection from './components/SearchSection/SearchSection';
import EarnMoneySection from './components/EarnMoneySection/EarnMoneySection';
import TermsConditions from './components/TermsConditions/TermsConditions';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import FeaturedMeals from './components/FeaturedMeals/FeaturedMeals';
import FoodDiscovery from './components/FoodDiscovery/FoodDiscovery';
import BrowseKitchens from './components/BrowseKitchens/BrowseKitchens';
import SellFoodModal from './components/SellFoodModal/SellFoodModal';
import DeliveryPartnerModal from './components/DeliveryPartnerModal/DeliveryPartnerModal';
import CommunityPlatformPolicies from './components/CommunityPlatformPolicies/CommunityPlatformPolicies';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './components/TermsOfService/TermsOfService';
import AboutUs from './components/About Us/AboutUs';
import FAQ from './components/FAQ/FAQ';
import CookiePolicy from './components/CookiePolicy/CookiePolicy';
import UserProfile from './components/UserProfile/UserProfile';
import LandingPage from './components/LandingPage/LandingPage';
import { trackPageView, trackAuth, trackError } from './components/Utils/analytics';
import { clearAuthData, getUserRoles } from './components/Utils/apiHelpers';
import { API_BASE_URL } from './config/api';
import { requestNotificationPermission, registerDeviceToken, registerDeviceTokenIfChanged, clearRegisteredDeviceToken } from './components/Utils/firebaseNotifications';
import { listenForForegroundMessages } from './services/firebaseMessaging';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';
import AdminRouteGuard from './components/Admin/AdminRouteGuard';
import ProfileCompletion from './components/ProfileCompletion/ProfileCompletion';
import RoleActionBanner from './components/RoleActionBanner/RoleActionBanner';
import Cart from './components/Cart/Cart';
import OrderSuccessModal from './components/OrderSuccessModal/OrderSuccessModal';
import { ChefHat, X } from 'lucide-react';
import LocationPermissionModal from './components/LocationPermissionModal/LocationPermissionModal';
import { getLocationPermissionState, setLocationPermissionState } from './components/Utils/location';
import { OrderSocketProvider } from './components/Utils/OrderSocketContext';

let foregroundListenerStarted = false;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PageTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(document.title, window.location.href);
  }, [location]);
  return null;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [showSellFoodModal, setShowSellFoodModal] = useState(false);
  const [showDeliveryPartnerModal, setShowDeliveryPartnerModal] = useState(false);
  const [showKitchenRequiredModal, setShowKitchenRequiredModal] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [selectedKitchenId, setSelectedKitchenId] = useState<number | null>(null);
  const [selectedKitchenName, setSelectedKitchenName] = useState<string | null>(null);

  // Cart state
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartRefreshToken, setCartRefreshToken] = useState(0);
  const [checkoutSuccessData, setCheckoutSuccessData] = useState<any>(null);
  const [locationVersion, setLocationVersion] = useState(0);
  const [showLocationModal, setShowLocationModal] = useState(
    () => getLocationPermissionState() === 'pending'
  );

  const refreshCart = () => setCartRefreshToken(t => t + 1);

  const { movedLocation, confirmLocationUpdate, dismissLocationUpdate, detectLocation } = useLocationFlow(isLoggedIn);

  const handleLocationConfirmed = async () => {
    await confirmLocationUpdate();
    setLocationVersion(v => v + 1);
  };

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isProfileCompletionRoute = location.pathname === '/complete-profile';

  React.useEffect(() => {
    const checkAuthStatus = async () => {
      const authToken = localStorage.getItem('authToken');
      const userEmail = localStorage.getItem('userEmail');

      if (!authToken || !userEmail) {
        setIsLoggedIn(false);
        setIsAuthChecked(true);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/users/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401 || response.status === 403) {
          clearAuthData();
          setIsLoggedIn(false);
        } else {
          setIsLoggedIn(true);
          setUserRoles(getUserRoles());
          setIsProfileComplete(localStorage.getItem('profileCompleted') !== 'false');

          registerDeviceTokenIfChanged().catch((err) => console.error('FCM registration error:', err));
          if (!foregroundListenerStarted) {
            listenForForegroundMessages();
            foregroundListenerStarted = true;
          }

          try {
            const json = await response.json();
            if (json?.profile?.kitchen?.kitchen_id) {
              localStorage.setItem('kitchenId', String(json.profile.kitchen.kitchen_id));
            } else {
              const kitchenRes = await fetch(`${API_BASE_URL}/api/v1/my-kitchen`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
              });
              if (kitchenRes.ok) {
                const kitchenJson = await kitchenRes.json();
                const kid = kitchenJson?.kitchen_id;
                if (kid) {
                  localStorage.setItem('kitchenId', String(kid));
                } else {
                  localStorage.removeItem('kitchenId');
                }
              } else {
                localStorage.removeItem('kitchenId');
              }
            }
          } catch {
            // ignore
          }
        }
      } catch (error) {
        console.error('Auth validation error:', error);
        trackError('auth_check_failed', 'app_init');
        setIsLoggedIn(true);
        setUserRoles(getUserRoles());
        setIsProfileComplete(localStorage.getItem('profileCompleted') !== 'false');
      } finally {
        setIsAuthChecked(true);
      }
    };

    checkAuthStatus();
  }, []);

  React.useEffect(() => {
    const onSessionExpired = () => handleLogout();
    window.addEventListener('sessionExpired', onSessionExpired);
    return () => window.removeEventListener('sessionExpired', onSessionExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isLoggedIn && isAuthChecked) {
      const handlePopState = () => {
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/signup') {
          window.history.replaceState(null, '', '/');
          window.location.reload();
        }
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [isLoggedIn, isAuthChecked]);

  const handleLogin = () => {
    setIsLoggedIn(true);
    setUserRoles(getUserRoles());
    setIsProfileComplete(localStorage.getItem('profileCompleted') !== 'false');
    trackAuth('login');

    console.log('FCM (handleLogin): Starting notification registration flow...');

    requestNotificationPermission()
      .then(async (token) => {
        if (token) {
          console.log('FCM (handleLogin): Permission granted, registering token...');
          const success = await registerDeviceToken(token);
          if (success) {
            console.log('FCM (handleLogin): Device token registered successfully.');
          } else {
            console.warn('FCM (handleLogin): Device token registration failed.');
          }
          return;
        }

        console.warn('FCM (handleLogin): requestNotificationPermission returned null.');

        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          console.log('FCM (handleLogin): Permission already granted — attempting direct getToken fallback...');
          try {
            const { getToken } = await import('firebase/messaging');
            const { messaging } = await import('./firebase');
            const { ensureServiceWorkerRegistered } = await import('./components/Utils/firebaseNotifications');
            const VAPID_KEY = 'BIFv5ltdZt0CBKW3qlO2y9e9lywqgr0GzsB2e4j-jI8hrGQX3rsU6PIr8IzZRQ39hdBTAbBwjYkr6Qux4ojN6kE';

            const swReady = await ensureServiceWorkerRegistered();
            if (!swReady) {
              console.warn('FCM (handleLogin): Service worker not available for fallback.');
              return;
            }

            const fallbackToken = await getToken(messaging, { vapidKey: VAPID_KEY });
            if (fallbackToken) {
              console.log('FCM (handleLogin): Fallback token retrieved, registering...');
              await registerDeviceToken(fallbackToken);
            } else {
              console.warn('FCM (handleLogin): Fallback getToken also returned null.');
            }
          } catch (fallbackErr) {
            console.error('FCM (handleLogin): Fallback getToken failed:', fallbackErr);
          }
        } else {
          console.warn('FCM (handleLogin): Notification permission is not "granted". Token will not be registered.');
        }
      })
      .catch((err) => console.error('FCM (handleLogin): Notification permission error:', err));

    if (!foregroundListenerStarted) {
      listenForForegroundMessages();
      foregroundListenerStarted = true;
    }

    detectLocation();
  };

  const handleProfileCompleted = () => {
    setIsProfileComplete(true);
    localStorage.setItem('profileCompleted', 'true');
  };

  const handleLogout = () => {
    clearAuthData();
    clearRegisteredDeviceToken();
    setIsLoggedIn(false);
    setUserRoles([]);
    setIsProfileComplete(true);
    setIsCartOpen(false);
    setCartRefreshToken(0);
    window.history.replaceState(null, '', '/');
    trackAuth('logout');
  };

  const handleShowSellFood = () => {
    if (!localStorage.getItem('kitchenId')) {
      setShowKitchenRequiredModal(true);
    } else {
      setShowSellFoodModal(true);
    }
  };

  if (!isAuthChecked) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e9ecef',
          borderTop: '4px solid var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <OrderSocketProvider>
    <AppContainer>
      <PageTracker />
      {!isAdminRoute && !isProfileCompletionRoute && (
        <Header
          isLoggedIn={isLoggedIn}
          onLogout={handleLogout}
          onCartClick={isLoggedIn && userRoles.includes('customer') ? () => setIsCartOpen(true) : undefined}
          cartRefreshToken={cartRefreshToken}
        />
      )}

      <MainContent>
        <Routes>
          <Route path="/login" element={
            isLoggedIn ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
          } />
          <Route path="/signup" element={
            isLoggedIn ? <Navigate to="/" replace /> : <SignUp />
          } />

          <Route path="/complete-profile" element={
            !isLoggedIn ? (
              <Navigate to="/login" replace />
            ) : isProfileComplete ? (
              <Navigate to="/home" replace />
            ) : (
              <ProfileCompletion onCompleted={handleProfileCompleted} />
            )
          } />

          <Route path="/profile" element={
            !isLoggedIn ? (
              <Navigate to="/login" replace />
            ) : !isProfileComplete ? (
              <Navigate to="/complete-profile" replace />
            ) : (
              <UserProfile onLogout={handleLogout} userRoles={userRoles} onCartChange={refreshCart} />
            )
          } />

          <Route path="/" element={
            !isLoggedIn ? (
              <LandingPage />
            ) : !isProfileComplete ? (
              <Navigate to="/complete-profile" replace />
            ) : (
              <Navigate to="/home" replace />
            )
          } />

          <Route path="/home" element={
            !isLoggedIn ? (
              <Navigate to="/login" replace />
            ) : !isProfileComplete ? (
              <Navigate to="/complete-profile" replace />
            ) : (
              <>
                <RoleActionBanner
                  userRoles={userRoles}
                  onSellFood={handleShowSellFood}
                  onDeliveryPartner={() => setShowDeliveryPartnerModal(true)}
                />
                <HeroSection
                  userRoles={userRoles}
                  onSellFood={handleShowSellFood}
                  onDeliveryPartner={() => setShowDeliveryPartnerModal(true)}
                />
                <SearchSection
                  onCartChange={refreshCart}
                  onKitchenSelect={(id, name) => {
                    setSelectedKitchenId(id);
                    setSelectedKitchenName(name);
                  }}
                />
                {userRoles.includes('customer') && (
                  <>
                    <FeaturedMeals
                      filteredKitchenId={selectedKitchenId}
                      filteredKitchenName={selectedKitchenName}
                      onClearFilter={() => { setSelectedKitchenId(null); setSelectedKitchenName(null); }}
                      onCartChange={refreshCart}
                      cartRefreshToken={cartRefreshToken}
                      locationVersion={locationVersion}
                    />
                    <FoodDiscovery onCartChange={refreshCart} cartRefreshToken={cartRefreshToken} locationVersion={locationVersion} />
                    <BrowseKitchens
                      selectedKitchenId={selectedKitchenId}
                      onSelectKitchen={(id, name) => {
                        setSelectedKitchenId(id);
                        setSelectedKitchenName(name);
                      }}
                      locationVersion={locationVersion}
                    />
                  </>
                )}
                {(userRoles.includes('kitchen_owner') || userRoles.includes('delivery_partner')) && (
                  <EarnMoneySection
                    onSellFood={handleShowSellFood}
                    onDeliveryPartner={() => setShowDeliveryPartnerModal(true)}
                    showSellFood={userRoles.includes('kitchen_owner')}
                    showDelivery={userRoles.includes('delivery_partner')}
                  />
                )}
              </>
            )
          } />

          <Route path="/about" element={<AboutUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/community-platform-policies" element={<CommunityPlatformPolicies />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <AdminRouteGuard>
              <AdminDashboard />
            </AdminRouteGuard>
          } />
        </Routes>
      </MainContent>

      <Footer />

      {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}

      {showSellFoodModal && <SellFoodModal onClose={() => setShowSellFoodModal(false)} />}

      {showDeliveryPartnerModal && (
        <DeliveryPartnerModal onClose={() => setShowDeliveryPartnerModal(false)} />
      )}

      {showKitchenRequiredModal && (
        <div
          onClick={() => setShowKitchenRequiredModal(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '20px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
              padding: '2.5rem 2rem 2rem',
              maxWidth: '440px',
              width: '100%',
              textAlign: 'center',
              position: 'relative',
              animation: 'kitchenModalIn 0.25s ease-out',
            }}
          >
            <button
              onClick={() => setShowKitchenRequiredModal(false)}
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#999', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '4px', borderRadius: '50%',
              }}
            >
              <X size={22} />
            </button>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
              <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: 'rgba(255, 107, 53, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ChefHat size={36} color="#ff6b35" />
              </div>
            </div>

            <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.75rem' }}>
              Kitchen Required to Sell Food
            </h2>

            <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '0.5rem' }}>
              To list and sell your food, you first need to set up a <strong>kitchen profile</strong>. Your kitchen is your storefront on HomeCookt — give it a name, add a description, and set your location.
            </p>
            <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>
              Once your kitchen is created, you'll be able to add food items and start receiving orders right away. It only takes a minute!
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={() => setShowKitchenRequiredModal(false)}
                style={{
                  padding: '0.7rem 1.4rem',
                  border: '1.5px solid #dee2e6',
                  borderRadius: '10px',
                  background: 'white',
                  color: '#555',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.92rem',
                }}
              >
                Maybe Later
              </button>
              <button
                onClick={() => { setShowKitchenRequiredModal(false); navigate('/profile?tab=kitchen'); }}
                style={{
                  padding: '0.7rem 1.6rem',
                  border: 'none',
                  borderRadius: '10px',
                  background: '#ff6b35',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                <ChefHat size={16} />
                Create My Kitchen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart drawer — only for customers */}
      {isLoggedIn && userRoles.includes('customer') && (
        <Cart
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          onCartChange={refreshCart}
          onCheckoutSuccess={(data) => {
            setCheckoutSuccessData(data);
          }}
        />
      )}

      {/* Order success modal after checkout */}
      {checkoutSuccessData && (
        <OrderSuccessModal
          orderData={checkoutSuccessData}
          onClose={() => setCheckoutSuccessData(null)}
          onViewOrders={() => { setCheckoutSuccessData(null); navigate('/profile?tab=my-orders'); }}
          onTrackOrder={() => { setCheckoutSuccessData(null); navigate('/profile?tab=active-orders'); }}
        />
      )}

      {/* Location movement toast */}
      {movedLocation && !isAdminRoute && !isProfileCompletionRoute && (
        <LocationToast
          movedLocation={movedLocation}
          onConfirm={handleLocationConfirmed}
          onDismiss={dismissLocationUpdate}
          onLocationSaved={() => setLocationVersion(v => v + 1)}
        />
      )}

      {/* Location permission modal — shown on first visit until resolved */}
      {showLocationModal && !isAdminRoute && (
        <LocationPermissionModal
          onGranted={() => {
            setShowLocationModal(false);
            setLocationVersion(v => v + 1);
          }}
          onManualCity={() => {
            setShowLocationModal(false);
            setLocationVersion(v => v + 1);
          }}
        />
      )}

      {/* Persistent banner for browser-blocked users on return visits */}
      {!showLocationModal && getLocationPermissionState() === 'denied_browser' && !isAdminRoute && !isProfileCompletionRoute && (
        <div
          style={{
            position: 'fixed',
            bottom: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fff3cd',
            border: '1.5px solid #ffc107',
            borderRadius: '12px',
            padding: '0.65rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            zIndex: 1500,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            maxWidth: '480px',
            width: 'calc(100% - 2rem)',
            fontSize: '0.9rem',
            color: '#856404',
          }}
        >
          <span style={{ flex: 1 }}>
            Location access is blocked — nearby features are limited.{' '}
            <button
              onClick={() => setShowLocationModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#ff6b35',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
                fontSize: 'inherit',
              }}
            >
              Enable location
            </button>
          </span>
          <button
            onClick={() => setLocationPermissionState('manual')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '2px', display: 'flex' }}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </AppContainer>
    </OrderSocketProvider>
  );
}

export default App;
